import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../app/BusinessDash/components/ui/dialog";
import { Clock, Award, BookOpen, CheckCircle, Monitor, MapPin, User, UsersRound, GraduationCap, BookMarked } from "lucide-react";
import { cn } from "../../app/BusinessDash/lib/utils";

const OptionButton = ({ selected, onClick, icon, label, price }) => (
  <button
    onClick={onClick}
    className={cn(
      "flex-1 flex flex-col items-center p-4 rounded-xl border-2 transition-all duration-200",
      selected
        ? "border-tier-education bg-tier-education/10"
        : "border-border/50 bg-card hover:border-tier-education/40 hover:bg-tier-education/5"
    )}
  >
    <div className={cn(
      "mb-2 transition-colors",
      selected ? "text-tier-education" : "text-muted-foreground"
    )}>
      {icon}
    </div>
    <span className={cn(
      "text-sm font-medium transition-colors text-center",
      selected ? "text-foreground" : "text-muted-foreground"
    )}>
      {label}
    </span>
    <span className={cn(
      "text-lg font-bold mt-1 transition-colors",
      selected ? "text-tier-education" : "text-foreground"
    )}>
      {price}
    </span>
  </button>
);

const ExamButton = ({ selected, onClick, icon, label, description, price }) => (
  <button
    onClick={onClick}
    className={cn(
      "flex-1 flex flex-col items-center p-3 rounded-xl border-2 transition-all duration-200",
      selected
        ? "border-tier-education bg-tier-education/10"
        : "border-border/50 bg-card hover:border-tier-education/40 hover:bg-tier-education/5"
    )}
  >
    <div className={cn(
      "mb-1.5 transition-colors",
      selected ? "text-tier-education" : "text-muted-foreground"
    )}>
      {icon}
    </div>
    <span className={cn(
      "text-sm font-medium transition-colors",
      selected ? "text-foreground" : "text-muted-foreground"
    )}>
      {label}
    </span>
    <span className="text-xs text-muted-foreground mt-0.5">{description}</span>
    {price && (
      <span className={cn(
        "text-sm font-semibold mt-1",
        selected ? "text-tier-education" : "text-foreground"
      )}>
        {price}
      </span>
    )}
  </button>
);

export const CourseDetailDialog = ({
  course,
  open,
  onOpenChange,
}) => {
  const [attendance, setAttendance] = useState("individual-online");
  const [examOption, setExamOption] = useState("without-exam");

  const totalPrice = useMemo(() => {
    let base = 0;
    switch (attendance) {
      case "individual-online":
        base = 3500;
        break;
      case "class-copenhagen":
      case "class-online":
        base = 4500;
        break;
    }
    const examCost = examOption === "with-exam" ? 1500 : 0;
    return base + examCost;
  }, [attendance, examOption]);

  if (!course) return null;

  const getSelectedSummary = () => {
    const attendanceLabels = {
      "individual-online": "Individual Online",
      "class-copenhagen": "Class Copenhagen",
      "class-online": "Class Online",
    };
    const parts = [
      attendanceLabels[attendance],
      examOption === "with-exam" ? "With Exam (ECTS)" : "Without Exam",
    ];
    return parts.join(" • ");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-tier-education">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-xl">{course.title}</DialogTitle>
            </div>
          </div>
          <DialogDescription className="text-left">
            {course.shortDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Course Options - Primary Selection */}
          <div className="space-y-4">
            <h4 className="font-semibold text-foreground">Select format and attendance</h4>
            
            {/* Attendance & Participation Type Combined */}
            <div className="grid grid-cols-3 gap-3">
              <OptionButton
                selected={attendance === "individual-online"}
                onClick={() => setAttendance("individual-online")}
                icon={<User className="h-6 w-6" />}
                label="Individual Online"
                price="3.500 kr"
              />
              <OptionButton
                selected={attendance === "class-copenhagen"}
                onClick={() => setAttendance("class-copenhagen")}
                icon={<MapPin className="h-6 w-6" />}
                label="Class Copenhagen"
                price="4.500 kr"
              />
              <OptionButton
                selected={attendance === "class-online"}
                onClick={() => setAttendance("class-online")}
                icon={<UsersRound className="h-6 w-6" />}
                label="Class Online"
                price="4.500 kr"
              />
            </div>

            {/* Exam Option */}
            <div>
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2 block">
                Exam
              </label>
              <div className="flex gap-3">
                <ExamButton
                  selected={examOption === "without-exam"}
                  onClick={() => setExamOption("without-exam")}
                  icon={<BookMarked className="h-5 w-5" />}
                  label="Without exam"
                  description="Course certificate"
                />
                <ExamButton
                  selected={examOption === "with-exam"}
                  onClick={() => setExamOption("with-exam")}
                  icon={<GraduationCap className="h-5 w-5" />}
                  label="With exam"
                  description="ECTS points"
                  price="+1,500 kr"
                />
              </div>
            </div>
          </div>

          {/* Price Summary */}
          <div className="p-4 rounded-xl bg-tier-education/10 border border-tier-education/30">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-muted-foreground">Your selected price:</p>
              <p className="text-2xl font-bold text-tier-education">
                {totalPrice.toLocaleString('en-US')} kr
              </p>
            </div>
            <p className="text-xs text-muted-foreground">excl. VAT • {getSelectedSummary()}</p>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col items-center p-3 rounded-xl bg-muted/50 border border-border/50">
              <Clock className="h-5 w-5 text-tier-education mb-1" />
              <span className="text-xs text-muted-foreground">Duration</span>
              <span className="text-sm font-medium">{course.duration}</span>
            </div>
            <div className="flex flex-col items-center p-3 rounded-xl bg-muted/50 border border-border/50">
              <Award className="h-5 w-5 text-tier-education mb-1" />
              <span className="text-xs text-muted-foreground">ECTS points</span>
              <span className="text-sm font-medium">{course.ects} ECTS</span>
            </div>
          </div>

          {/* Full Description */}
          <div>
            <h4 className="font-semibold text-foreground mb-2">About the course</h4>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {course.fullDescription}
            </p>
          </div>

          {/* Topics Covered */}
          <div>
            <h4 className="font-semibold text-foreground mb-3">Content</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {course.topics.map((topic, index) => (
                <div key={index} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-tier-education shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{topic}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Button */}
          <button
            className={cn(
              "w-full rounded-xl py-3 px-4 text-sm font-semibold transition-all duration-200",
              "bg-tier-education text-white hover:opacity-90 hover:shadow-lg"
            )}
          >
            Sign up for this course
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};






